import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, withCtx as _withCtx, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, createBlock as _createBlock } from "vue"
import _imports_0 from '@/assets/images/card-user-guide.webp'
import _imports_1 from '@/assets/images/card-upload.webp'
import _imports_2 from '@/assets/images/card-data.webp'
import _imports_3 from '@/assets/images/card-analytics.webp'


const _hoisted_1 = { class: "cards-grid" }
const _hoisted_2 = { class: "cards-grid__item" }
const _hoisted_3 = { class: "cards-grid__item" }
const _hoisted_4 = { class: "cards-grid__item" }
const _hoisted_5 = { class: "cards-grid__item" }
const _hoisted_6 = { class: "mt-4" }
const _hoisted_7 = { class: "mt-4" }
const _hoisted_8 = { class: "mt-4" }
const _hoisted_9 = { key: 0 }
const _hoisted_10 = { class: "mt-4" }
const _hoisted_11 = { class: "mt-4" }
const _hoisted_12 = { key: 1 }
const _hoisted_13 = { class: "mt-4" }
const _hoisted_14 = {
  key: 0,
  class: "card-text"
}
const _hoisted_15 = {
  key: 1,
  class: "card-text"
}

import { computed, ref } from 'vue';
import {
  BUCKET_DATA_TYPES,
  COMPUTE_TASK_TYPES,
  IS_DEBUG_MODE,
  SHARING_USER_ROLES,
} from '@/constants';
import { useUserStore } from '@/store/user';
import { useUploadsStore } from '@/store/uploads';
import { useClipboard } from '@vueuse/core';
import {
  BAlert,
  BButton,
  BButtonGroup,
  BCard,
  BCardText,
  BSpinner,
  BTooltip,
} from 'bootstrap-vue-next';
import { BucketsLiteDocument, ComputeTasksLiteDocument } from '@/gql/graphql';
import { GRAPHQL_ENDPOINT_URL } from '@/apollo/client';
import { KEYCLOAK_URL } from '@/auth/keycloak';
import ComputeTaskList from '@/components/compute/ComputeTaskList.vue';
import BucketList from '@/components/data/BucketList.vue';
import WebUploadList from '@/components/upload/WebUploadList.vue';
import { formatDateTimeToIso } from '@/utils/date-utils';
import { openRouterLink } from '@/utils/router-utils';
import AnalyticsGuide from '@/components/AnalyticsGuide.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'DashboardView',
  setup(__props) {

const userStore = useUserStore();
const uploadsStore = useUploadsStore();

// --- Component References ---
const dataPipelineListRef = ref<InstanceType<typeof ComputeTaskList>>();
const dataTransferListRef = ref<InstanceType<typeof ComputeTaskList>>();
const webUploadListRef = ref<InstanceType<typeof WebUploadList>>();
const bucketListRef = ref<InstanceType<typeof BucketList>>();
const keycloakAccountUrl =
  process.env.VUE_APP_KEYCLOAK_URL +
  '/realms/' +
  process.env.VUE_APP_KEYCLOAK_REALM +
  '/account';

// --- Component State ---
const showAnalyticsGuideModal = ref(false);
const limitDateTimeRecent = computed(() => {
  const limiteDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
  return formatDateTimeToIso(limiteDate);
});

const isLoading = computed(() => {
  return (
    dataPipelineListRef.value?.tasksQueryInProgress ||
    dataTransferListRef.value?.tasksQueryInProgress ||
    bucketListRef.value?.bucketsQueryInProgress
  );
});

const isError = computed(() => {
  return (
    !isLoading.value &&
    (dataPipelineListRef.value?.tasksQueryError ||
      dataTransferListRef.value?.tasksQueryError ||
      bucketListRef.value?.bucketsQueryError ||
      uploadsStore.error)
  );
});

const errorMessage = computed(() => {
  let msg = '';
  if (dataPipelineListRef.value?.tasksQueryError) {
    msg += `Error Fetching Sample Reconstruction Details: ${dataPipelineListRef.value.tasksQueryError.message}\n`;
  }
  if (dataTransferListRef.value?.tasksQueryError) {
    msg += `Error Fetching Data Transfer Details: ${dataTransferListRef.value.tasksQueryError.message}\n`;
  }
  if (bucketListRef.value?.bucketsQueryError) {
    msg += `Error Fetching Recent Data: ${bucketListRef.value.bucketsQueryError.message}\n`;
  }
  return msg;
});

const invitesPending = computed(() => {
  return userStore.pendingInvites > 0;
});

function refetchDashboardDetails() {
  dataPipelineListRef.value?.requestSearch();
  dataTransferListRef.value?.requestSearch();
  bucketListRef.value?.requestSearch();
  // no need to refetch uploads (they are being polled by the app)
}

/* JWT TOKEN DETAILS *************************************************/
function copyUuid() {
  if (userStore.uuid) {
    const clip = useClipboard();
    clip.copy(userStore.uuid);
    showUuidTooltip.value = true;

    // Hide the tooltip after 2 seconds
    setTimeout(() => {
      showUuidTooltip.value = false;
    }, 2000);
  } else {
    alert('UUID not available');
  }
}

function copyAuth() {
  if (userStore.authHeader) {
    const clip = useClipboard();
    clip.copy(userStore.authHeader);
    showAuthTooltip.value = true;

    // Hide the tooltip after 2 seconds
    setTimeout(() => {
      showAuthTooltip.value = false;
    }, 2000);
  } else {
    alert('Authorization details not available');
  }
}

const showUuidTooltip = ref(false);
const showAuthTooltip = ref(false);

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("h1", null, [
      _createVNode(_component_font_awesome_icon, { icon: ['fa', 'home'] }),
      _cache[19] || (_cache[19] = _createTextVNode(" Dashboard ", -1))
    ]),
    _cache[75] || (_cache[75] = _createElementVNode("h5", null, "Welcome to Sampling Human Cloud Analytics!", -1)),
    _cache[76] || (_cache[76] = _createElementVNode("hr", null, null, -1)),
    _createVNode(_unref(BAlert), {
      variant: "warning",
      modelValue: invitesPending.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((invitesPending).value = $event))
    }, {
      default: _withCtx(() => [
        _createElementVNode("h5", null, [
          _cache[20] || (_cache[20] = _createTextVNode(" You have ", -1)),
          _createElementVNode("strong", null, _toDisplayString(_unref(userStore).pendingInvites) + " pending invite", 1),
          _createTextVNode(" " + _toDisplayString(_unref(userStore).pendingInvites === 1 ? 'request' : 'requests') + " to join organizations ", 1)
        ]),
        _createElementVNode("p", null, [
          _cache[22] || (_cache[22] = _createTextVNode(" Visit the ", -1)),
          _createVNode(_component_router_link, {
            to: { name: 'OrganizationSettings' },
            class: "text-decoration-none fw-bold"
          }, {
            default: _withCtx(() => [...(_cache[21] || (_cache[21] = [
              _createTextVNode(" organization settings ", -1)
            ]))]),
            _: 1
          }),
          _cache[23] || (_cache[23] = _createTextVNode(" page to accept or decline them. ", -1))
        ]),
        _createVNode(_unref(BButton), {
          to: { name: 'OrganizationSettings' },
          variant: "warning",
          class: "text-decoration-none"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_font_awesome_icon, {
              icon: ['fas', 'users'],
              class: "me-1"
            }),
            _cache[24] || (_cache[24] = _createTextVNode(" Organization Settings ", -1))
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _cache[77] || (_cache[77] = _createElementVNode("p", null, " Browse through your recent projects, upload data, analyze and inspect results. Access premium analysis features. Manage your account and organization information. ", -1)),
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createVNode(_unref(BCard), {
          title: "Analysis in six steps",
          "img-src": _imports_0,
          "img-top": "",
          class: "dashboard-card",
          "body-class": "d-flex flex-column",
          "footer-class": "mt-auto"
        }, {
          footer: _withCtx(() => [
            _createVNode(_unref(BButton), {
              onClick: _cache[1] || (_cache[1] = ($event: any) => (showAnalyticsGuideModal.value = true)),
              variant: "outline-dark",
              size: "sm",
              class: "w-100"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_font_awesome_icon, {
                  icon: "book",
                  class: "me-1"
                }),
                _cache[41] || (_cache[41] = _createTextVNode(" Read the Full Instructions ", -1))
              ]),
              _: 1
            })
          ]),
          default: _withCtx(() => [
            _createVNode(_unref(BCardText), null, {
              default: _withCtx(() => [
                _createElementVNode("ol", null, [
                  _cache[40] || (_cache[40] = _createElementVNode("li", null, " Extract DNA/RNA with a Sampling Human kit and save the kit code. ", -1)),
                  _createElementVNode("li", null, [
                    _cache[26] || (_cache[26] = _createTextVNode(" Import sequencing data using the ", -1)),
                    _createVNode(_component_router_link, { to: { name: 'UploadManager' } }, {
                      default: _withCtx(() => [...(_cache[25] || (_cache[25] = [
                        _createTextVNode("Upload Manager", -1)
                      ]))]),
                      _: 1
                    }),
                    _cache[27] || (_cache[27] = _createTextVNode(". ", -1))
                  ]),
                  _createElementVNode("li", null, [
                    _cache[29] || (_cache[29] = _createTextVNode(" Annotate samples in ", -1)),
                    _createVNode(_component_router_link, { to: { name: 'DataManager' } }, {
                      default: _withCtx(() => [...(_cache[28] || (_cache[28] = [
                        _createTextVNode("Data Manager", -1)
                      ]))]),
                      _: 1
                    }),
                    _cache[30] || (_cache[30] = _createTextVNode(". ", -1))
                  ]),
                  _createElementVNode("li", null, [
                    _cache[32] || (_cache[32] = _createTextVNode(" Create a ", -1)),
                    _createVNode(_component_router_link, { to: { name: 'Projects' } }, {
                      default: _withCtx(() => [...(_cache[31] || (_cache[31] = [
                        _createTextVNode("Project", -1)
                      ]))]),
                      _: 1
                    }),
                    _cache[33] || (_cache[33] = _createTextVNode(" and associate samples. ", -1))
                  ]),
                  _createElementVNode("li", null, [
                    _cache[35] || (_cache[35] = _createTextVNode(" Launch Sample Reconstruction from the ", -1)),
                    _createVNode(_component_router_link, { to: { name: 'Projects' } }, {
                      default: _withCtx(() => [...(_cache[34] || (_cache[34] = [
                        _createTextVNode("Project", -1)
                      ]))]),
                      _: 1
                    }),
                    _cache[36] || (_cache[36] = _createTextVNode(". ", -1))
                  ]),
                  _createElementVNode("li", null, [
                    _cache[38] || (_cache[38] = _createTextVNode(" Review and download results in the ", -1)),
                    _createVNode(_component_router_link, { to: { name: 'Projects' } }, {
                      default: _withCtx(() => [...(_cache[37] || (_cache[37] = [
                        _createTextVNode("Project", -1)
                      ]))]),
                      _: 1
                    }),
                    _cache[39] || (_cache[39] = _createTextVNode(". ", -1))
                  ])
                ])
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _createElementVNode("div", _hoisted_3, [
        _createVNode(_unref(BCard), {
          title: "Upload Manager",
          "img-src": _imports_1,
          "img-top": "",
          class: "dashboard-card",
          "body-class": "d-flex flex-column",
          "footer-class": "mt-auto"
        }, {
          footer: _withCtx(() => [
            _createVNode(_unref(BButton), {
              onClick: _cache[2] || (_cache[2] = ($event: any) => (
                _unref(openRouterLink)({ name: 'UploadManager' }, _ctx.$router, $event)
              )),
              variant: "outline-dark",
              size: "sm",
              class: "w-100"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_font_awesome_icon, {
                  icon: "upload",
                  class: "me-1"
                }),
                _cache[43] || (_cache[43] = _createTextVNode(" Go to Upload Manager ", -1))
              ]),
              _: 1
            })
          ]),
          default: _withCtx(() => [
            _createVNode(_unref(BCardText), null, {
              default: _withCtx(() => [...(_cache[42] || (_cache[42] = [
                _createElementVNode("p", null, " Upload FASTQs directly or transfer directly from your sequencing provider via FTP. ", -1)
              ]))]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _createElementVNode("div", _hoisted_4, [
        _createVNode(_unref(BCard), {
          title: "Data Management",
          "img-src": _imports_2,
          "img-alt": "Data",
          "img-top": "",
          class: "dashboard-card",
          "body-class": "d-flex flex-column",
          "footer-class": "mt-auto"
        }, {
          footer: _withCtx(() => [
            _createVNode(_unref(BButton), {
              onClick: _cache[3] || (_cache[3] = ($event: any) => (_unref(openRouterLink)({ name: 'DataManager' }, _ctx.$router, $event))),
              variant: "outline-dark",
              size: "sm",
              class: "w-100"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_font_awesome_icon, {
                  icon: "database",
                  class: "me-1"
                }),
                _cache[45] || (_cache[45] = _createTextVNode(" Go to Data Manager ", -1))
              ]),
              _: 1
            })
          ]),
          default: _withCtx(() => [
            _createVNode(_unref(BCardText), null, {
              default: _withCtx(() => [...(_cache[44] || (_cache[44] = [
                _createElementVNode("p", null, " Browse and annotate samples and share them between projects to get the most out of your data. ", -1)
              ]))]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _createElementVNode("div", _hoisted_5, [
        _createVNode(_unref(BCard), {
          title: "Cloud Analysis",
          "img-src": _imports_3,
          "img-alt": "Analytics",
          "img-top": "",
          class: "dashboard-card",
          "body-class": "d-flex flex-column",
          "footer-class": "mt-auto"
        }, {
          footer: _withCtx(() => [
            _createVNode(_unref(BButton), {
              onClick: _cache[4] || (_cache[4] = ($event: any) => (_unref(openRouterLink)({ name: 'Projects' }, _ctx.$router, $event))),
              variant: "outline-dark",
              size: "sm",
              class: "w-100"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_font_awesome_icon, {
                  icon: "list",
                  class: "me-1"
                }),
                _cache[47] || (_cache[47] = _createTextVNode(" Go to Projects ", -1))
              ]),
              _: 1
            })
          ]),
          default: _withCtx(() => [
            _createVNode(_unref(BCardText), null, {
              default: _withCtx(() => [...(_cache[46] || (_cache[46] = [
                _createElementVNode("p", null, " Powerful analysis of transcriptomic data at your fingertips. Organize your projects, analyze and inspect results. ", -1)
              ]))]),
              _: 1
            })
          ]),
          _: 1
        })
      ])
    ]),
    _createVNode(_unref(BAlert), {
      modelValue: isLoading.value,
      "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((isLoading).value = $event)),
      variant: "secondary",
      class: "mt-4"
    }, {
      default: _withCtx(() => [
        _cache[48] || (_cache[48] = _createTextVNode(" Loading data ", -1)),
        _createVNode(_unref(BSpinner), {
          label: "Loading...",
          small: ""
        })
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(_unref(BAlert), {
      modelValue: isError.value,
      "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((isError).value = $event)),
      variant: "danger",
      class: "mt-4"
    }, {
      default: _withCtx(() => [
        _createTextVNode(_toDisplayString(errorMessage.value) + " ", 1),
        _createVNode(_unref(BButton), {
          onClick: refetchDashboardDetails,
          variant: "outline-dark",
          class: "btn-sm"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_font_awesome_icon, { icon: "sync" }),
            _cache[49] || (_cache[49] = _createTextVNode(" Retry ", -1))
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createElementVNode("div", {
      class: _normalizeClass([{ 'd-none': isLoading.value }])
    }, [
      _createElementVNode("div", {
        class: _normalizeClass([{ 'd-none': !dataPipelineListRef.value?.tasks.length }])
      }, [
        _createElementVNode("h4", _hoisted_6, [
          _createVNode(_component_font_awesome_icon, { icon: ['fa', 'line-chart'] }),
          _cache[50] || (_cache[50] = _createTextVNode(" Recent sample reconstructions ", -1))
        ]),
        _createVNode(ComputeTaskList, {
          ref_key: "dataPipelineListRef",
          ref: dataPipelineListRef,
          class: "mb-1",
          "parent-name": "Dashboard",
          "user-role": _unref(SHARING_USER_ROLES).OWNER,
          "task-types": [_unref(COMPUTE_TASK_TYPES).DATA_PIPELINE],
          "updated-after": limitDateTimeRecent.value,
          "paging-items-per-page": 5,
          "paging-hide-controls": true,
          "show-bucket-button": false,
          "show-exec-details": false,
          "show-abort-button": false,
          "message-no-items-found": 'No samples reconstructed recently.',
          "transparent-style": true,
          "query-document": _unref(ComputeTasksLiteDocument),
          "task-name-column-title": "Sample Reconstruction"
        }, null, 8, ["user-role", "task-types", "updated-after", "query-document"]),
        _createVNode(_unref(BButton), {
          onClick: _cache[7] || (_cache[7] = ($event: any) => (_unref(openRouterLink)({ name: 'Projects' }, _ctx.$router, $event))),
          variant: "outline-dark",
          size: "sm",
          class: "mb-2"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_font_awesome_icon, {
              icon: "diagram-project",
              class: "me-1"
            }),
            _cache[51] || (_cache[51] = _createTextVNode(" See all projects ", -1))
          ]),
          _: 1
        })
      ], 2),
      _createElementVNode("div", {
        class: _normalizeClass([{ 'd-none': !bucketListRef.value?.buckets.length }])
      }, [
        _createElementVNode("h4", _hoisted_7, [
          _createVNode(_component_font_awesome_icon, { icon: ['fa', 'database'] }),
          _cache[52] || (_cache[52] = _createTextVNode(" Recently added data ", -1))
        ]),
        _createVNode(BucketList, {
          ref_key: "bucketListRef",
          ref: bucketListRef,
          class: "mb-1",
          parentName: "Dashboard",
          "data-type": _unref(BUCKET_DATA_TYPES).INPUT,
          "created-after": limitDateTimeRecent.value,
          "paging-items-per-page": 5,
          "paging-hide-controls": true,
          "show-user-role": false,
          "show-bucket-description": false,
          "message-no-items-found": 'No new data were recently added to your collection.',
          "transparent-style": true,
          "query-document": _unref(BucketsLiteDocument)
        }, null, 8, ["data-type", "created-after", "query-document"]),
        _createVNode(_unref(BButton), {
          onClick: _cache[8] || (_cache[8] = ($event: any) => (_unref(openRouterLink)({ name: 'DataManager' }, _ctx.$router, $event))),
          variant: "outline-dark",
          size: "sm",
          class: "mb-2"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_font_awesome_icon, {
              icon: "database",
              class: "me-1"
            }),
            _cache[53] || (_cache[53] = _createTextVNode(" Go to Data Manager ", -1))
          ]),
          _: 1
        })
      ], 2),
      _createElementVNode("div", {
        class: _normalizeClass([{ 'd-none': !dataTransferListRef.value?.tasks.length }])
      }, [
        _createElementVNode("h4", _hoisted_8, [
          _createVNode(_component_font_awesome_icon, {
            icon: "arrow-right-arrow-left",
            class: "me-1"
          }),
          _cache[54] || (_cache[54] = _createTextVNode(" Recent data transfers ", -1))
        ]),
        _createVNode(ComputeTaskList, {
          ref_key: "dataTransferListRef",
          ref: dataTransferListRef,
          class: "mb-1",
          "parent-name": "Dashboard",
          "user-role": _unref(SHARING_USER_ROLES).OWNER,
          "task-types": [
            _unref(COMPUTE_TASK_TYPES).FTP_TRANSFER,
            _unref(COMPUTE_TASK_TYPES).BASESPACE_TRANSFER,
          ],
          "updated-after": limitDateTimeRecent.value,
          "paging-items-per-page": 5,
          "paging-hide-controls": true,
          "show-exec-details": false,
          "show-abort-button": false,
          "message-no-items-found": 'No recent data transfers found.',
          "transparent-style": true,
          "query-document": _unref(ComputeTasksLiteDocument),
          "task-name-column-title": "Transfer"
        }, null, 8, ["user-role", "task-types", "updated-after", "query-document"]),
        _createVNode(_unref(BButton), {
          onClick: _cache[9] || (_cache[9] = ($event: any) => (_ctx.$router.push({ name: 'UploadManager' }))),
          variant: "outline-dark",
          size: "sm",
          class: "mb-2"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_font_awesome_icon, {
              icon: "arrow-right-arrow-left",
              class: "me-1"
            }),
            _cache[55] || (_cache[55] = _createTextVNode(" See all data transfers ", -1))
          ]),
          _: 1
        })
      ], 2)
    ], 2),
    (!_unref(uploadsStore).loading && _unref(uploadsStore).uploads.length > 0)
      ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
          _createElementVNode("h4", _hoisted_10, [
            _createVNode(_component_font_awesome_icon, {
              icon: "cloud-arrow-up",
              class: "me-1"
            }),
            _cache[56] || (_cache[56] = _createTextVNode(" Recent uploads ", -1))
          ]),
          _createVNode(WebUploadList, {
            ref_key: "webUploadListRef",
            ref: webUploadListRef,
            class: "mb-1",
            "transparent-style": true
          }, null, 512),
          _createVNode(_unref(BButton), {
            onClick: _cache[10] || (_cache[10] = ($event: any) => (_unref(openRouterLink)({ name: 'UploadManager' }, _ctx.$router, $event))),
            variant: "outline-dark",
            size: "sm",
            class: "mb-2"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_font_awesome_icon, {
                icon: "cloud-arrow-up",
                class: "me-1"
              }),
              _cache[57] || (_cache[57] = _createTextVNode(" See all uploads ", -1))
            ]),
            _: 1
          })
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("h4", _hoisted_11, [
      _createVNode(_component_font_awesome_icon, { icon: ['fa', 'file-import'] }),
      _cache[58] || (_cache[58] = _createTextVNode(" Import new data ", -1))
    ]),
    _cache[78] || (_cache[78] = _createElementVNode("p", null, " Use one of the options below to import your FASTQ files and get them ready for analysis: ", -1)),
    _cache[79] || (_cache[79] = _createElementVNode("ol", null, [
      _createElementVNode("li", null, [
        _createElementVNode("strong", null, "Upload FASTQs"),
        _createTextVNode(" from your computer.")
      ]),
      _createElementVNode("li", null, [
        _createTextVNode("Transfer files from an "),
        _createElementVNode("strong", null, "FTP server"),
        _createTextVNode(".")
      ]),
      _createElementVNode("li", null, [
        _createTextVNode(" Connect to your "),
        _createElementVNode("strong", null, "Illumina's BaseSpace Sequence Hub"),
        _createTextVNode(" and select files for import. ")
      ])
    ], -1)),
    _createVNode(_unref(BButton), {
      onClick: _cache[11] || (_cache[11] = ($event: any) => (_unref(openRouterLink)({ name: 'UploadManager' }, _ctx.$router, $event))),
      variant: "outline-dark",
      size: "sm",
      class: "mb-2"
    }, {
      default: _withCtx(() => [
        _createVNode(_component_font_awesome_icon, {
          icon: "file-import",
          class: "me-1"
        }),
        _cache[59] || (_cache[59] = _createTextVNode(" Import new data ", -1))
      ]),
      _: 1
    }),
    (_unref(IS_DEBUG_MODE) || _unref(userStore).developer)
      ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
          _cache[74] || (_cache[74] = _createElementVNode("hr", { class: "mt-4" }, null, -1)),
          _createElementVNode("h3", _hoisted_13, [
            _createVNode(_component_font_awesome_icon, { icon: ['fa', 'code'] }),
            _cache[60] || (_cache[60] = _createTextVNode(" Developer Information ", -1))
          ]),
          (!_unref(userStore).id)
            ? (_openBlock(), _createElementBlock("p", _hoisted_14, "Loading..."))
            : (_openBlock(), _createElementBlock("p", _hoisted_15, [
                _cache[61] || (_cache[61] = _createElementVNode("strong", null, "User ID:", -1)),
                _createTextVNode(" " + _toDisplayString(_unref(userStore).id), 1),
                _cache[62] || (_cache[62] = _createElementVNode("br", null, null, -1)),
                _cache[63] || (_cache[63] = _createElementVNode("strong", null, "Email:", -1)),
                _createTextVNode(" " + _toDisplayString(_unref(userStore).email), 1),
                _cache[64] || (_cache[64] = _createElementVNode("br", null, null, -1))
              ])),
          _createVNode(_unref(BButtonGroup), {
            class: "btn-group btn-group-sm me-1 mb-1",
            role: "group",
            "aria-label": "Authorization Buttons"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(BButton), {
                variant: "outline-dark",
                disabled: "",
                "aria-disabled": "true"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_font_awesome_icon, { icon: ['fa', 'copy'] })
                ]),
                _: 1
              }),
              _createVNode(_unref(BButton), {
                id: "copy-button-uuid",
                variant: "outline-dark",
                onClick: _cache[12] || (_cache[12] = _withModifiers(($event: any) => (copyUuid()), ["prevent"]))
              }, {
                default: _withCtx(() => [...(_cache[65] || (_cache[65] = [
                  _createTextVNode(" UUID ", -1)
                ]))]),
                _: 1
              }),
              _createVNode(_unref(BButton), {
                id: "copy-button-auth",
                variant: "outline-dark",
                onClick: _cache[13] || (_cache[13] = _withModifiers(($event: any) => (copyAuth()), ["prevent"]))
              }, {
                default: _withCtx(() => [...(_cache[66] || (_cache[66] = [
                  _createTextVNode(" Authorization header ", -1)
                ]))]),
                _: 1
              })
            ]),
            _: 1
          }),
          (showUuidTooltip.value)
            ? (_openBlock(), _createBlock(_unref(BTooltip), {
                key: 2,
                modelValue: showUuidTooltip.value,
                "onUpdate:modelValue": _cache[14] || (_cache[14] = ($event: any) => ((showUuidTooltip).value = $event)),
                ref: "uuidTooltip",
                target: "copy-button-uuid",
                placement: "top"
              }, {
                default: _withCtx(() => [...(_cache[67] || (_cache[67] = [
                  _createTextVNode(" UUID copied ", -1)
                ]))]),
                _: 1
              }, 8, ["modelValue"]))
            : _createCommentVNode("", true),
          (showAuthTooltip.value)
            ? (_openBlock(), _createBlock(_unref(BTooltip), {
                key: 3,
                modelValue: showAuthTooltip.value,
                "onUpdate:modelValue": _cache[15] || (_cache[15] = ($event: any) => ((showAuthTooltip).value = $event)),
                ref: "authTooltip",
                target: "copy-button-auth",
                placement: "top"
              }, {
                default: _withCtx(() => [...(_cache[68] || (_cache[68] = [
                  _createTextVNode(" Authorization header copied ", -1)
                ]))]),
                _: 1
              }, 8, ["modelValue"]))
            : _createCommentVNode("", true),
          _createVNode(_unref(BButton), {
            variant: "outline-dark",
            class: "me-1 mb-1",
            size: "sm",
            target: "_blank",
            href: _unref(GRAPHQL_ENDPOINT_URL)
          }, {
            default: _withCtx(() => [
              _createVNode(_component_font_awesome_icon, {
                icon: "play",
                class: "me-1"
              }),
              _cache[69] || (_cache[69] = _createTextVNode(" GraphQL Playground ", -1))
            ]),
            _: 1
          }, 8, ["href"]),
          _createVNode(_unref(BButton), {
            variant: "outline-dark",
            class: "me-1 mb-1",
            size: "sm",
            target: "_blank",
            href: _unref(KEYCLOAK_URL)
          }, {
            default: _withCtx(() => [
              _createVNode(_component_font_awesome_icon, {
                icon: "shield-halved",
                class: "me-1"
              }),
              _cache[70] || (_cache[70] = _createTextVNode(" IAM: Keycloak Console ", -1))
            ]),
            _: 1
          }, 8, ["href"]),
          _createVNode(_unref(BButton), {
            variant: "outline-dark",
            class: "me-1 mb-1",
            size: "sm",
            href: keycloakAccountUrl,
            target: "_blank"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_font_awesome_icon, {
                icon: "user",
                class: "me-1"
              }),
              _cache[71] || (_cache[71] = _createTextVNode(" Keycloak Account ", -1))
            ]),
            _: 1
          }),
          (_unref(KEYCLOAK_URL).includes('localhost'))
            ? (_openBlock(), _createBlock(_unref(BButton), {
                key: 4,
                variant: "outline-dark",
                class: "me-1 mb-1",
                size: "sm",
                target: "_blank",
                href: "http://localhost:8025"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_font_awesome_icon, {
                    icon: "envelope",
                    class: "me-1"
                  }),
                  _cache[72] || (_cache[72] = _createTextVNode(" MailHog ", -1))
                ]),
                _: 1
              }))
            : _createCommentVNode("", true),
          (_unref(KEYCLOAK_URL).includes('localhost'))
            ? (_openBlock(), _createBlock(_unref(BButton), {
                key: 5,
                variant: "outline-dark",
                class: "me-1 mb-1",
                size: "sm",
                target: "_blank",
                href: "http://localhost:8081/?pgsql=backend-db&username=app_user&db=app_db"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_font_awesome_icon, {
                    icon: "database",
                    class: "me-1"
                  }),
                  _cache[73] || (_cache[73] = _createTextVNode(" PostgreSQL Adminer ", -1))
                ]),
                _: 1
              }))
            : _createCommentVNode("", true),
          _createVNode(_unref(BButton), {
            onClick: _cache[16] || (_cache[16] = ($event: any) => (_unref(userStore).admin = !_unref(userStore).admin)),
            variant: "outline-dark",
            class: "me-1 mb-1",
            size: "sm"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_font_awesome_icon, {
                icon: _unref(userStore).admin ? 'user-slash' : 'user',
                class: "me-1"
              }, null, 8, ["icon"]),
              _createTextVNode(" " + _toDisplayString(_unref(userStore).admin ? 'Disable' : 'Enable') + " Admin Settings ", 1)
            ]),
            _: 1
          }),
          _createVNode(_unref(BButton), {
            onClick: _cache[17] || (_cache[17] = ($event: any) => (_unref(userStore).developer = !_unref(userStore).developer)),
            variant: "outline-dark",
            class: "me-1 mb-1",
            size: "sm"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_font_awesome_icon, {
                icon: _unref(userStore).developer ? 'bug-slash' : 'bug',
                class: "me-1"
              }, null, 8, ["icon"]),
              _createTextVNode(" " + _toDisplayString(_unref(userStore).developer ? 'Disable' : 'Enable') + " Developer Settings ", 1)
            ]),
            _: 1
          })
        ]))
      : _createCommentVNode("", true),
    (showAnalyticsGuideModal.value)
      ? (_openBlock(), _createBlock(AnalyticsGuide, {
          key: 2,
          show: showAnalyticsGuideModal.value,
          "onUpdate:show": _cache[18] || (_cache[18] = ($event: any) => ((showAnalyticsGuideModal).value = $event))
        }, null, 8, ["show"]))
      : _createCommentVNode("", true)
  ]))
}
}

})